/* eslint-disable react/prop-types */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { createPortal } from 'react-dom';
import { MdGroups } from 'react-icons/md';
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../services/api';

import {
  Overlay,
  Modal,
  ModalHeader,
  HeaderTitle,
  CloseButton,
  Toolbar,
  ToolbarInfo,
  ToolbarSpacer,
  Body,
  Loading,
  TableWrapper,
  Badge,
} from '../BIDetectTextJobModal/styles';

/* ------------------------------------------------------------------ *
 * paleta / tokens (alinhados ao tema do BI)
 * ------------------------------------------------------------------ */
const C = {
  ink: '#17263b',
  muted: '#64748b',
  border: 'rgba(216, 224, 235, 0.9)',
  primary: '#ff6d3c',
  blue: '#1789dd',
  green: '#16a34a',
  soft: '#f8fafc',
  white: '#fff',
};

/* ------------------------------------------------------------------ *
 * formatação
 * ------------------------------------------------------------------ */
function formatDate(value) {
  if (!value) return '-';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatNumber(value) {
  if (value === null || value === undefined) return '0';

  return Number(value).toLocaleString('pt-BR');
}

function formatMinutes(value) {
  if (value === null || value === undefined) return '-';

  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) return '-';

  if (numericValue < 60) return `${Math.round(numericValue)} min`;

  const hour = Math.floor(numericValue / 60);
  const min = Math.round(numericValue % 60);

  if (hour < 24) return `${hour}h ${min}min`;

  const days = Math.floor(hour / 24);
  const remainingHours = hour % 24;

  return `${days}d ${remainingHours}h`;
}

/* ------------------------------------------------------------------ *
 * estilos
 * ------------------------------------------------------------------ */
const styles = {
  page: { display: 'flex', flexDirection: 'column', gap: 20 },

  sectionLabel: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    margin: '0 0 10px',
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: C.muted,
  },
  sectionCount: { fontSize: 12, color: C.muted },

  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 12,
  },
  metricTile: (accent) => ({
    background: C.white,
    border: `1px solid ${C.border}`,
    borderLeft: `3px solid ${accent}`,
    borderRadius: 12,
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  }),
  metricLabel: { fontSize: 12, color: C.muted },
  metricValue: { fontSize: 22, fontWeight: 700, color: C.ink, lineHeight: 1.1 },
  metricHint: { fontSize: 11, color: C.muted },

  twoCols: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: 16,
    alignItems: 'start',
  },

  panel: {
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  panelHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: `1px solid ${C.border}`,
    background: C.soft,
  },
  panelTitle: { fontSize: 14, fontWeight: 700, color: C.ink, margin: 0 },
  panelCount: { fontSize: 12, color: C.muted },

  empGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 10,
    padding: 12,
    maxHeight: 460,
    overflowY: 'auto',
  },
  empRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    padding: 12,
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    background: C.white,
  },
  rank: {
    flexShrink: 0,
    width: 26,
    height: 26,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    color: C.white,
    background: C.blue,
  },
  empBody: { flex: 1, minWidth: 0 },
  empTopLine: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 8,
  },
  empName: {
    fontSize: 13,
    fontWeight: 600,
    color: C.ink,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  empId: { fontSize: 11, color: C.muted, fontWeight: 400 },
  empTotal: { fontSize: 15, fontWeight: 700, color: C.primary, flexShrink: 0 },
  barTrack: {
    height: 6,
    borderRadius: 999,
    background: 'rgba(23,137,221,0.12)',
    margin: '6px 0',
    overflow: 'hidden',
  },
  barFill: (pct) => ({
    height: '100%',
    width: `${pct}%`,
    borderRadius: 999,
    background: `linear-gradient(90deg, ${C.blue}, ${C.primary})`,
    transition: 'width 0.3s ease',
  }),
  statsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'center',
  },
  stat: { display: 'flex', gap: 4, alignItems: 'baseline', fontSize: 12 },
  statValue: { fontWeight: 700, color: C.ink },
  statLabel: { color: C.muted },
  empLast: { marginLeft: 'auto', fontSize: 11, color: C.muted },

  empty: { padding: 20, textAlign: 'center', color: C.muted, fontSize: 13 },
};

/* ------------------------------------------------------------------ *
 * sub-componentes
 * ------------------------------------------------------------------ */
function SectionTitle({ title, count }) {
  return (
    <div style={styles.sectionLabel}>
      <span style={styles.sectionTitle}>{title}</span>
      {count !== undefined && <span style={styles.sectionCount}>{count}</span>}
    </div>
  );
}

function PanelHead({ title, count }) {
  return (
    <div style={styles.panelHead}>
      <h3 style={styles.panelTitle}>{title}</h3>
      {count !== undefined && <span style={styles.panelCount}>{count}</span>}
    </div>
  );
}

function MetricTile({ label, value, hint, accent }) {
  return (
    <div style={styles.metricTile(accent || C.blue)}>
      <span style={styles.metricLabel}>{label}</span>
      <span style={styles.metricValue}>{value}</span>
      {hint ? <span style={styles.metricHint}>{hint}</span> : null}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <span style={styles.stat}>
      <span style={styles.statValue}>{value}</span>
      <span style={styles.statLabel}>{label}</span>
    </span>
  );
}

function EmpresaRow({ rank, item, max }) {
  const total = Number(item.totalAcessos || 0);
  const pct = max > 0 ? Math.max((total / max) * 100, 2) : 0;

  const nome = item.nomeEmpresa || `Empresa ${item.idEmpresa}`;

  return (
    <div style={styles.empRow}>
      <div style={styles.rank}>{rank}</div>

      <div style={styles.empBody}>
        <div style={styles.empTopLine}>
          <span style={styles.empName} title={nome}>
            {nome} <span style={styles.empId}>#{item.idEmpresa}</span>
          </span>
          <span style={styles.empTotal}>{formatNumber(total)}</span>
        </div>

        <div style={styles.barTrack}>
          <div style={styles.barFill(pct)} />
        </div>

        <div style={styles.statsRow}>
          <Stat label="usuários" value={formatNumber(item.usuariosDistintos)} />
          <Stat label="hoje" value={formatNumber(item.acessosHoje)} />
          <Stat label="30d" value={formatNumber(item.acessosUltimos30Dias)} />
          <span style={styles.empLast}>
            último: {formatDate(item.ultimoAcesso)}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * modal
 * ------------------------------------------------------------------ */
export function BIAcessosPlataformaModal({ open, onClose }) {
  const [limit, setLimit] = useState(20);
  const [empresa, setEmpresa] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  async function loadBI(currentLimit = limit, currentEmpresa = empresa) {
    setLoading(true);

    try {
      const params = { limit: currentLimit || 20 };

      if (currentEmpresa) params.empresa = currentEmpresa;

      const biResponse = await api.get('/bi/acessos-plataforma', { params });

      setData(biResponse.data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) {
      setEmpresa('');
      loadBI(20, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const empresas = data?.acessosPorEmpresa || [];

  const maxAcessos = useMemo(
    () =>
      empresas.reduce(
        (acc, item) => Math.max(acc, Number(item.totalAcessos || 0)),
        0
      ),
    [empresas]
  );

  if (!open) return null;

  const resumo = data?.resumo;

  return createPortal(
    <Overlay>
      <Modal>
        <ModalHeader>
          <div>
            <HeaderTitle>
              <MdGroups size={30} />
              <h2>BI - Acessos Plataforma</h2>
            </HeaderTitle>
            <span>
              Monitoramento de acessos (logins) dos usuários por empresa
            </span>
          </div>

          <CloseButton type="button" onClick={onClose}>
            ×
          </CloseButton>
        </ModalHeader>

        <Toolbar>
          <label>
            Registros na lista
            <input
              type="number"
              min={1}
              max={200}
              value={limit}
              onChange={(event) => setLimit(Number(event.target.value))}
            />
          </label>

          <label>
            Empresa
            <select
              value={empresa}
              onChange={(event) => setEmpresa(event.target.value)}
            >
              <option value="">Todas</option>

              {empresas.map((item) => (
                <option
                  key={String(item.idEmpresa)}
                  value={String(item.idEmpresa)}
                >
                  {item.nomeEmpresa
                    ? `${item.nomeEmpresa} (#${item.idEmpresa})`
                    : `Empresa ${item.idEmpresa}`}
                </option>
              ))}
            </select>
          </label>

          <button type="button" onClick={() => loadBI(limit, empresa)}>
            Atualizar
          </button>

          <ToolbarInfo>
            <span>Empresas com acesso</span>
            <strong>{formatNumber(resumo?.totalEmpresasComAcesso)}</strong>
          </ToolbarInfo>

          <ToolbarSpacer />

          <ToolbarInfo>
            <span>Total de acessos (geral)</span>
            <strong>{formatNumber(resumo?.totalAcessosGeral)}</strong>
          </ToolbarInfo>
        </Toolbar>

        <Body>
          {loading && <Loading>Carregando BI...</Loading>}

          {!loading && resumo && (
            <div style={styles.page}>
              {/* ---------- Resumo ---------- */}
              <section>
                <SectionTitle
                  title={`Resumo ${
                    empresa ? '· empresa selecionada' : '· todas as empresas'
                  }`}
                />

                <div style={styles.metricsGrid}>
                  <MetricTile
                    label="Total de acessos"
                    value={formatNumber(resumo.totalAcessos)}
                    accent={C.blue}
                  />
                  <MetricTile
                    label="Acessos hoje"
                    value={formatNumber(resumo.acessosHoje)}
                    accent={C.primary}
                  />
                  <MetricTile
                    label="Últimas 24h"
                    value={formatNumber(resumo.acessosUltimas24h)}
                    accent={C.primary}
                  />
                  <MetricTile
                    label="Últimos 7 dias"
                    value={formatNumber(resumo.acessosUltimos7Dias)}
                    accent={C.blue}
                  />
                  <MetricTile
                    label="Últimos 30 dias"
                    value={formatNumber(resumo.acessosUltimos30Dias)}
                    accent={C.blue}
                  />
                  <MetricTile
                    label="Usuários distintos"
                    value={formatNumber(resumo.usuariosDistintos)}
                    accent={C.green}
                  />
                  <MetricTile
                    label="Último acesso"
                    value={formatDate(resumo.ultimoAcesso)}
                    hint={`há ${formatMinutes(
                      resumo.minutosDesdeUltimoAcesso
                    )}`}
                    accent={C.green}
                  />
                  <MetricTile
                    label="Primeiro registro"
                    value={formatDate(resumo.primeiroAcesso)}
                    accent={C.muted}
                  />
                </div>
              </section>

              {/* ---------- 2 colunas: por empresa + últimos acessos ---------- */}
              <div style={styles.twoCols}>
                <section style={styles.panel}>
                  <PanelHead
                    title="Últimos acessos"
                    count={`${data?.acessos?.length || 0} registros`}
                  />

                  <TableWrapper>
                    <table>
                      <thead>
                        <tr>
                          <th>Data/Hora</th>
                          <th>Usuário</th>
                          <th>Login</th>
                          <th>Empresa</th>
                        </tr>
                      </thead>

                      <tbody>
                        {data?.acessos?.map((item, index) => (
                          <tr key={`acesso-${index}`}>
                            <td>{formatDate(item.INSTINC)}</td>
                            <td>
                              <Badge variant="success">{item.IDUSUARIO}</Badge>{' '}
                              {item.NOME || '-'}
                            </td>
                            <td>{item.LOGIN || '-'}</td>
                            <td>
                              {item.nomeEmpresa || `#${item.IDEMPRESA}` || '-'}
                            </td>
                          </tr>
                        ))}

                        {(!data?.acessos || data.acessos.length === 0) && (
                          <tr>
                            <td colSpan={4}>Nenhum acesso registrado.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </TableWrapper>
                </section>

                <section style={styles.panel}>
                  <PanelHead
                    title="Acessos por empresa"
                    count={`${empresas.length} empresas`}
                  />

                  {empresas.length === 0 ? (
                    <div style={styles.empty}>Nenhum acesso registrado.</div>
                  ) : (
                    <div style={styles.empGrid}>
                      {empresas.map((item, index) => (
                        <EmpresaRow
                          key={String(item.idEmpresa)}
                          rank={index + 1}
                          item={item}
                          max={maxAcessos}
                        />
                      ))}
                    </div>
                  )}
                </section>
              </div>
            </div>
          )}
        </Body>
      </Modal>
    </Overlay>,
    document.body
  );
}
